import { formulaParser } from '../lezer-syntax-parser';

import { buildTokensTree, getAllTokens, getTokenByPath } from './syntax-tree-builder';

export enum SimpleSyntaxTypes {
  concatenation = 'concatenation',
  array = 'array',
  literal = 'literal',
  singleValue = 'singleValue',
  unknown = 'unknown',
}

export const checkIfItsVariable = (code: string): boolean => {
  const hexIdRegex = /^[a-fA-F0-9]{24}$/;
  const isHexId = code.split('-').every((part) => part.length === 24 && hexIdRegex.test(part));
  return (
    code.startsWith('$$') ||
    code.includes('colId:') ||
    code.startsWith('@@') ||
    isHexId ||
    code.startsWith('tagId:')
  );
};

export const hasSyntaxErrors = (formula: string): boolean => {
  const treeNodes = buildTokensTree(formula, formulaParser);
  const allTokens = getAllTokens(treeNodes);
  return allTokens.some((token) => token.name === '⚠');
};

export const getSimpleSyntaxType = (formula: string): SimpleSyntaxTypes => {
  const treeNodes = buildTokensTree(formula, formulaParser);

  //some backward com for old formulas with literal values for arrays etc
  if (
    treeNodes.children.length > 0 &&
    treeNodes.children.every((node) => node.name === '⚠') &&
    treeNodes.text.length
  ) {
    return SimpleSyntaxTypes.literal;
  }

  const allTokens = getAllTokens(treeNodes);
  if (allTokens.some((token) => token.name === '⚠')) {
    return SimpleSyntaxTypes.unknown;
  }
  const topLevelNodes = getTokenByPath(treeNodes, ['Expression', '*']);
  const secondLeveLNodes = getTokenByPath(treeNodes, ['Expression', '*', '*']);

  if (topLevelNodes.length === 0) {
    return SimpleSyntaxTypes.singleValue;
  }

  const singleExpression = secondLeveLNodes.length === 1 ? secondLeveLNodes[0] : undefined;

  if (singleExpression) {
    if (['String', 'VariableName', 'Number'].includes(singleExpression.name)) {
      return SimpleSyntaxTypes.singleValue;
    }
    if (singleExpression.name === 'Array') {
      const arrayParts = getTokenByPath(singleExpression, ['*', '*', '*']);
      const arrayPartTypes = arrayParts.map((node) => {
        if (node.name === 'String') {
          const parsedString = JSON.parse(node.text);
          if (checkIfItsVariable(parsedString)) {
            return 'VariableName';
          }
          return node.name;
        }
        return node.name;
      });

      const noSiblingStrings = arrayPartTypes.every((type, index, arr) => {
        if (index === 0) {
          return true;
        }
        return type !== 'String' || arr[index - 1] !== 'String';
      });
      const onlyStringsAndVariables = arrayPartTypes.every((type) =>
        ['String', 'VariableName', 'Number'].includes(type),
      );
      if (noSiblingStrings && onlyStringsAndVariables) {
        return SimpleSyntaxTypes.array;
      }
    }
  }

  const onlyPlusesAndSingleExpressions = topLevelNodes.every((node) => {
    if (node.name === 'Operator') {
      return node.text === '+';
    }
    return node.name === 'SingleExpression';
  });

  const isSimpleSum =
    onlyPlusesAndSingleExpressions &&
    topLevelNodes.some((node) => {
      return node.name === 'Operator';
    });

  const onlyStringsAndVariables = secondLeveLNodes.every((node) =>
    ['String', 'VariableName'].includes(node.name),
  );

  if (isSimpleSum && onlyStringsAndVariables) {
    return SimpleSyntaxTypes.concatenation;
  }

  return SimpleSyntaxTypes.unknown;
};
